import {
  Action,
  ActionReducerMapBuilder,
  createSlice,
  PayloadAction,
  SliceCaseReducers,
} from "@reduxjs/toolkit";
import { CreateJobForm } from "models/createJobForm";
import { CreateJobValidationSchema } from "models/validation/CreateJobValidationSchema";
import { CustomerType, JobSymptomType, MachineType } from "operations/schema/schema";
import { RootState } from "store";
import { ValidationError } from "yup";
import { asyncMutations, mutationBuilder } from "./createJob.mutations";
import { asyncQueries, queryBuilder } from "./createJob.queries";

// Interface for state
export interface State {
  createJobLoading: boolean;
  initLoading: boolean;
  isDirty: boolean;
  isValid: boolean;
  open: boolean;
  customers: CustomerType[];
  customersLoading: boolean;
  machines: MachineType[];
  machinesLoading: boolean;
  errors: any;
  symptoms: JobSymptomType[];
  formValues: CreateJobForm;
}
/** Example state interface
 * feedback: boolean
 */

// Interface for store reducers
interface Reducers extends SliceCaseReducers<State> {
  setOpen: (state: State, action: PayloadAction<{ open: boolean }>) => State;
  validateForm: (state: State, action: Action) => State;
  setValue: <K extends Extract<keyof CreateJobForm, string>>(
    state: State,
    action: PayloadAction<{
      field: K;
      value: CreateJobForm[K];
      shouldValidate?: boolean;
    }>
  ) => State;
  handleClose: (state: State, action: Action) => State;
}

// Interface for store selectors (if necessary)
interface Selectors {
  selectCreateJobOpen: (state: RootState) => boolean;
  selectCreateJobOptions: (state: RootState) => State;
  selectCreateJobForm: (state: RootState) => CreateJobForm;
}

// Definition of actual (initial) state
export const initialState: State = {
  initLoading: false,
  createJobLoading: false,
  isValid: true,
  isDirty: false,
  open: false,
  customers: [],
  customersLoading: false,
  machines: [],
  machinesLoading: false,
  errors: {},
  symptoms: [],
  formValues: {
    engineer: null,
    customer: null,
    description: "",
    machine: null,
    files: [],
    symptomCode1: null,
  },
};

// Definition of actual reducers
const reducers: Reducers = {
  createJobOnQueued: (state, { payload }) => {},
  setOpen: (state, { payload: { open } }) => {
    state.open = open;
    return state;
  },
  setValue: (state, { payload: { field, value, shouldValidate } }) => {
    const { isValid } = state;
    state.formValues[field] = value;
    state.isDirty = true;

    if (field === "customer") {
      let customer = value as CustomerType;
      if (customer) {
        state.machines = state.machines.filter((e) => e.customerId === customer.id);
        if (state.formValues.machine?.customerId !== customer.id) {
          state.formValues.machine = null;
        }
      }
    }

    if (!shouldValidate && isValid) return state;
    const validationSchema = CreateJobValidationSchema();
    const isValidated = validationSchema.isValidSync(state.formValues);
    if (!isValidated) {
      try {
        validationSchema.validateSync({ ...state.formValues }, { strict: true, abortEarly: false });
      } catch (e: any) {
        let { inner } = e as ValidationError;
        let errors: any = {};
        for (let error of inner) {
          if (!error.path) continue;
          errors[error.path] = error.message;
        }
        state.errors = { ...errors };
      }
      state.isValid = false;
    } else {
      state.isValid = true;
      state.errors = {};
    }
    return state;
  },
  validateForm: (state) => {
    const validationSchema = CreateJobValidationSchema();
    const isValidated = validationSchema.isValidSync(state.formValues);
    if (!isValidated) {
      try {
        validationSchema.validateSync({ ...state.formValues }, { strict: true, abortEarly: false });
      } catch (e: any) {
        let { inner } = e as ValidationError;
        let errors: any = {};
        for (let error of inner) {
          if (!error.path) continue;
          errors[error.path] = error.message;
        }
        state.errors = { ...errors };
      }
      state.isValid = false;
    } else {
      state.isValid = true;
      state.errors = {};
    }
    return state;
  },
  handleClose: (state) => {
    state.open = false;
    state.initLoading = false;
    state.createJobLoading = false;
    state.isValid = true;
    state.isDirty = false;
    state.errors = {};
    state.formValues = {
      engineer: null,
      customer: null,
      description: "",
      machine: null,
      files: [],
      symptomCode1: null,
    };
    return state;
  },
};

// Definition of actual selectors
const selectors: Selectors = {
  selectCreateJobOpen: ({ createJob }) => {
    return createJob.open;
  },
  selectCreateJobOptions: ({ createJob }) => {
    return createJob;
  },
  selectCreateJobForm: ({ createJob }) => {
    return createJob.formValues;
  },
};
/** Example function
 * selectFeedback: ({dialog}) => dialog.feedback
 */

const storeBase = createSlice<State, Reducers>({
  name: "createJob",
  initialState,
  reducers,
  extraReducers: (builder: ActionReducerMapBuilder<State>) => {
    queryBuilder(builder);
    mutationBuilder(builder);
  },
});

// To be imported and added in store/reducers
export default storeBase.reducer;

// Export all actions created in store, to be used in components
// I.e, if using setOpen defined in examples above, it would look like this
// export const { setOpen } = storeBase.actions;
export const { setOpen, setValue, validateForm, handleClose } = storeBase.actions;

export const { getCreateJobOptions, getCustomer, getCustomers, getCustomerByMachine, getMachines } =
  asyncQueries;

export const { createJob } = asyncMutations;

// Export all selectors created in store, to be used in components
// I.e, if using selectFeedback defined in examples above, it would look like this
// export const { selectFeedback } = selectors;
export const { selectCreateJobOpen, selectCreateJobOptions, selectCreateJobForm } = selectors;
